import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation } from 'react-router';
import { ApiError } from '@crave/api-client';
import { AppUpdateBar, isTyping, reloadOnMissingCode, useAppUpdate } from '@crave/ui';
import '@crave/ui/tokens.css';
import { App } from './App.tsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once on network trouble or a 5xx. A 4xx (signed out, no access, bad request)
      // will not fix itself, so surface it right away instead of waiting on a retry.
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 408 &&
          error.status !== 429
        )
          return false;
        return failureCount < 1;
      },
      staleTime: 5_000,
    },
  },
});

if (import.meta.env.PROD) reloadOnMissingCode();

/**
 * The kitchen tablet hangs on the wall, so a newer deploy loads by itself as soon as no request
 * is running and nobody is typing. The queue comes straight back after the reload.
 */
function UpdateWatcher() {
  const { pathname, search } = useLocation();
  const { action, reload } = useAppUpdate({
    policy: 'kitchen',
    currentVersion: import.meta.env['VITE_BUILD_ID'] as string | undefined,
    enabled: import.meta.env.PROD,
    path: pathname,
    search,
    isBusy: () => queryClient.isFetching() > 0 || queryClient.isMutating() > 0 || isTyping(),
  });
  return action === 'show-bar' ? <AppUpdateBar onReload={reload} /> : null;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {window.location.pathname.startsWith('/reset-password') ? <ResetPasswordPage /> : <App />}
        <UpdateWatcher />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
